import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

interface SummaryMetric {
  label: string;
  value: string;
  subValue?: string;
}

interface ChartPoint {
  period: string;
  nav: number;
  cashflow: number;
  pnl: number;
}

interface FactItem {
  label: string;
  value: string;
}

interface SecurityRow {
  name: string;
  type: string;
  currency: string;
  value: string;
  commitment: string;
  exposure: string;
  status: string;
}

@Component({
  selector: 'app-fund-overview',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fund-overview.component.html',
  styleUrl: './fund-overview.component.scss'
})
export class FundOverviewComponent {
  readonly railIcons = ['◫', '⌕', '⌂', '▤', '◒', '▧', '⌁', '🛒', '□', '⚖', '▱', '☁', '⚙'];

  readonly tabs = [
    'Overview',
    'Transactions',
    'Balance',
    'Cashflow',
    'Profit and Loss',
    'Inventory card',
    'Documents',
    'Linked investments',
    'J-Curve',
    'J-Curve Planning'
  ];

  readonly sideLinks = [
    'Summary',
    'Facts',
    'Security Information',
    'Fund Information',
    'Key figures',
    'Properties',
    'Management Fee',
    'Exposures',
    'Fund Manager',
    'Fund Information'
  ];

  readonly summaryMetrics: SummaryMetric[] = [
    { label: 'Duration', value: '11/30/2018 - 11/29/2028', subValue: '11/30/2018 - 11/29/2028' },
    { label: 'Fund Volume', value: '760,870,000', subValue: '711,261,276' },
    { label: 'Commitment Date', value: '12/1/2018', subValue: '12/1/2018' },
    { label: 'Initial Commitment', value: '5,000,000', subValue: '4,674,000' },
    { label: 'Total Commitment', value: '5,000,000', subValue: '4,674,000' },
    { label: 'Open Commitment', value: '486,145', subValue: '454,448' },
    { label: 'Multiple', value: '1.57', subValue: '1.57' },
    { label: 'PIC Multiple', value: '0.90', subValue: '0.90' }
  ];

  readonly chartData: ChartPoint[] = [
    { period: '18/01 [A]', nav: 0, cashflow: 0, pnl: 0 },
    { period: '19/01 [A]', nav: 0, cashflow: 0, pnl: 0 },
    { period: '20/01 [A]', nav: 1_500_000, cashflow: -1_500_000, pnl: 0 },
    { period: '21/01 [A]', nav: 5_000_000, cashflow: -4_500_000, pnl: 500_000 },
    { period: '22/01 [A]', nav: 5_500_000, cashflow: -4_500_000, pnl: 1_000_000 },
    { period: '23/01 [A]', nav: 6_000_000, cashflow: -4_000_000, pnl: 2_000_000 },
    { period: '24/01 [A]', nav: 5_000_000, cashflow: -3_000_000, pnl: 2_000_000 },
    { period: '25/01 [F]', nav: 5_500_000, cashflow: -3_250_000, pnl: 2_150_000 },
    { period: '26/01 [F]', nav: 3_600_000, cashflow: -200_000, pnl: 3_700_000 },
    { period: '27/01 [F]', nav: 1_800_000, cashflow: 2_500_000, pnl: 4_400_000 },
    { period: '28/01 [F]', nav: 500_000, cashflow: 4_700_000, pnl: 5_000_000 },
    { period: '28/11 [F]', nav: 0, cashflow: 5_000_000, pnl: 5_100_000 }
  ];

  readonly factItems: FactItem[] = [
    { label: 'Classification', value: 'Illiquid assets > Private equity > Venture' },
    { label: 'Investment number', value: '334' },
    { label: 'Inherited from', value: 'Security' },
    { label: 'Investment vehicle', value: 'Closed-end fund' }
  ];

  readonly securityRows: SecurityRow[] = [
    {
      name: 'Hodgson Capital Partners',
      type: 'Private equity',
      currency: 'EUR',
      value: '5,013,855',
      commitment: '5,000,000',
      exposure: '486,145',
      status: 'Active'
    },
    {
      name: 'Co-investment sleeve',
      type: 'Venture',
      currency: 'CHF',
      value: '4,674,000',
      commitment: '4,674,000',
      exposure: '454,448',
      status: 'Active'
    },
    {
      name: 'Follow-on reserve',
      type: 'Reserve',
      currency: 'EUR',
      value: '760,870',
      commitment: '900,000',
      exposure: '139,130',
      status: 'Planned'
    }
  ];

  readonly yTicks = [6_000_000, 4_000_000, 2_000_000, 0, -2_000_000, -4_000_000, -6_000_000];

  private readonly minY = -6_000_000;
  private readonly maxY = 6_000_000;
  private readonly chartWidth = 1000;
  private readonly chartHeight = 220;
  private readonly paddingLeft = 72;
  private readonly paddingRight = 24;
  private readonly paddingTop = 10;
  private readonly paddingBottom = 42;

  linePoints(key: 'nav' | 'cashflow' | 'pnl'): string {
    return this.chartData.map((point, index) => `${this.x(index)},${this.y(point[key])}`).join(' ');
  }

  areaPoints(key: 'nav' | 'cashflow' | 'pnl'): string {
    const points = this.linePoints(key);
    const lastX = this.x(this.chartData.length - 1);
    const firstX = this.x(0);
    const zeroY = this.y(0);
    return `${firstX},${zeroY} ${points} ${lastX},${zeroY}`;
  }

  x(index: number): number {
    const drawable = this.chartWidth - this.paddingLeft - this.paddingRight;
    return this.paddingLeft + (drawable / (this.chartData.length - 1)) * index;
  }

  y(value: number): number {
    const drawable = this.chartHeight - this.paddingTop - this.paddingBottom;
    const normalized = (value - this.minY) / (this.maxY - this.minY);
    return this.paddingTop + drawable - normalized * drawable;
  }

  formatTick(value: number): string {
    return value.toLocaleString('en-US');
  }
}
